import { useContext, useState, useMemo } from 'react'
import { Context } from '@entities/Context'
import { useNavigate } from 'react-router-dom'
import { isEmpty, trim, values, flattenDeep } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faCircleNotch, faReply } from '@fortawesome/free-solid-svg-icons'

// Moved regex patterns outside component to avoid recreation
const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
const pinRegex = /^[0-9]{4,8}$/i

export default function Login() {

    const { api, storage } = useContext(Context)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [formData, setFormData] = useState({
        email: '',
        pin: ''
    })

    // Computed values as memoized values
    const invalidEmail = useMemo(() =>
        !isEmpty(formData.email) && !emailRegex.test(formData.email),
        [formData.email]
    )

    const invalidPin = useMemo(() =>
        !isEmpty(formData.pin) && !pinRegex.test(formData.pin),
        [formData.pin]
    )

    const disabled = useMemo(() =>
        loading ||
        isEmpty(formData.email) ||
        isEmpty(formData.pin) ||
        invalidEmail ||
        invalidPin,
        [loading, formData.email, formData.pin, invalidEmail, invalidPin]
    )

    // Event handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target
        let newValue = value

        // PIN validation (numbers only)
        if (name === 'pin') {
            newValue = value.replace(/[^0-9]+/, '')
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }))
    }

    const handleBack = () => {
        navigate('/welcome', { replace: true })
    }

    const handleLogin = async () => {
        if (loading) return
        setLoading(true)
        setError(false)

        try {
            const { data } = await api.get('/api/account', {
                params: {
                    email: formData.email,
                    pin: formData.pin,
                }
            })

            storage.set('pin', formData.pin)
            storage.set('account', {
                id: data.data.id,
                name: data.data.name,
                email: data.data.email,
                permissions: data.data.permissions,
                metadata: data.data.metadata,
            })

            navigate('/dashboard', { replace: true })
        } catch (error) {
            console.log(error)
            setError(trim(
                error.response.data.message +
                ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
            ))
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <p className="mb-6 text-lg font-light">
                Please take a moment to sign in to your account you already created. You'll then
                be able to pull up your giving records and make additional contributions.
            </p>

            {error && (
                <div className="relative px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            <div className="mb-4">
                <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="email">
                    Email Address
                </label>
                <input
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        invalidEmail ? 'border-red-500 bg-red-100' : ''
                    }`}
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="e.g.: johndoe@example.com"
                    inputMode="email"
                />
            </div>

            <div className="mb-6">
                <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="pin">
                    Security PIN
                </label>
                <input
                    value={formData.pin}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        invalidPin ? 'border-red-500 bg-red-100' : ''
                    }`}
                    id="pin"
                    name="pin"
                    type="password"
                    required
                    maxLength={8}
                    inputMode="numeric"
                />
            </div>

            <div className="flex flex-wrap justify-between">
                <button
                    onClick={handleLogin}
                    disabled={disabled}
                    className="px-6 py-3 mb-4 font-bold text-white rounded bg-primary disabled:bg-blue-400 disabled:cursor-not-allowed"
                    type="button"
                >
                    {loading ? (
                        <span>
                            <FontAwesomeIcon icon={faCircleNotch} className="fas animate-spin fa-lg" />
                        </span>
                    ) : (
                        <span>
                            <FontAwesomeIcon icon={faCheckCircle} className="fas fa-lg" />
                        </span>
                    )}
                    <span className="inline-block ml-3">
                        {loading ? 'Authenticating...' : 'Sign In'}
                    </span>
                </button>

                <button
                    onClick={handleBack}
                    className="text-gray-500 truncate hover:text-primary"
                >
                    <FontAwesomeIcon icon={faReply}className="mr-2 fas" />
                    Return
                </button>
            </div>
        </div>
    )
}
